
import { supabase } from '@/lib/supabase';
import { MarkupCalculationResult } from './types';
import { LineItemMarkupTier } from '@/types/line-item-markup-tiers';
import { hasValidCustomTiers, findApplicableTier } from './tierUtils';

export const calculateCustomTierMarkup = (
  customTiers: LineItemMarkupTier[],
  amount: number,
  useCustomTiers: boolean,
  instanceId: string
): MarkupCalculationResult | null => {
  console.log(`[useMarkupCalculation:${instanceId}] Step 1: Checking custom tiers`);
  
  if (useCustomTiers && hasValidCustomTiers(customTiers)) {
    console.log(`[useMarkupCalculation:${instanceId}] Using custom tiers:`, customTiers);
    const applicableTier = findApplicableTier(customTiers, amount);
    
    if (applicableTier) {
      return {
        markup: applicableTier.markup_percentage,
        margin: applicableTier.margin_percentage,
        finalPrice: amount * (1 + applicableTier.markup_percentage / 100),
        sourceName: 'custom',
        sourceLabel: 'Custom Tier',
        applicableTier
      };
    }
  }
  
  return null;
};

export const calculateCategoryMarkup = async (
  categoryId: string,
  companyId: string,
  amount: number,
  categoryName: string | undefined,
  instanceId: string
): Promise<MarkupCalculationResult | null> => {
  console.log(`[useMarkupCalculation:${instanceId}] Step 2: Checking category tiers`);
  console.log(`[useMarkupCalculation:${instanceId}] Calling category functions with:`, { categoryId, amount, companyId });
  
  try {
    const [markupResponse, marginResponse] = await Promise.all([
      supabase.rpc('get_category_markup', { 
        p_category_id: categoryId,
        p_amount: amount,
        p_company_id: companyId
      }),
      supabase.rpc('get_category_margin', { 
        p_category_id: categoryId,
        p_amount: amount,
        p_company_id: companyId
      })
    ]);
    
    console.log(`[useMarkupCalculation:${instanceId}] Category markup response:`, markupResponse);
    console.log(`[useMarkupCalculation:${instanceId}] Category margin response:`, marginResponse);
    
    if (markupResponse.error) throw markupResponse.error;
    if (marginResponse.error) throw marginResponse.error;
    
    const isValidMarkup = typeof markupResponse.data === 'number' && markupResponse.data >= 0;
    console.log(`[useMarkupCalculation:${instanceId}] Category validation - isValidMarkup:`, isValidMarkup, 'data:', markupResponse.data, 'type:', typeof markupResponse.data);
    
    if (isValidMarkup) {
      const result = {
        markup: markupResponse.data,
        margin: marginResponse.data || 0,
        finalPrice: amount * (1 + markupResponse.data / 100),
        sourceName: 'category' as const,
        sourceLabel: categoryName ? `Category: ${categoryName}` : 'Category Tier',
        categoryName: categoryName
      };
      
      console.log(`[useMarkupCalculation:${instanceId}] Category tier result - SETTING FINAL:`, result);
      return result;
    } else {
      console.log(`[useMarkupCalculation:${instanceId}] No valid category tier data, proceeding to global`);
    }
  } catch (err) {
    console.error(`[useMarkupCalculation:${instanceId}] Error fetching category tiers:`, err);
  }
  
  return null;
};

export const calculateGlobalMarkup = async (
  companyId: string,
  amount: number,
  instanceId: string
): Promise<MarkupCalculationResult> => {
  console.log(`[useMarkupCalculation:${instanceId}] Step 3: Using global fallback`);
  
  try {
    console.log(`[useMarkupCalculation:${instanceId}] Fetching global settings for company:`, companyId);
    const { data: settings, error: settingsError } = await supabase
      .from('cost_center_settings')
      .select('default_markup, default_margin')
      .eq('company_id', companyId)
      .maybeSingle();
      
    console.log(`[useMarkupCalculation:${instanceId}] Global settings response:`, settings, settingsError);
      
    if (settingsError && settingsError.code !== 'PGRST116') {
      throw settingsError;
    }
    
    if (settings) {
      const result = {
        markup: settings.default_markup || 0,
        margin: settings.default_margin || 0,
        finalPrice: amount * (1 + (settings.default_markup || 0) / 100),
        sourceName: 'global' as const,
        sourceLabel: 'Global Default'
      };
      console.log(`[useMarkupCalculation:${instanceId}] Global fallback result:`, result);
      return result;
    }
  } catch (err) {
    console.error(`[useMarkupCalculation:${instanceId}] Error fetching global settings:`, err);
  }
  
  // Final fallback
  return {
    markup: 0,
    margin: 0,
    finalPrice: amount,
    sourceName: 'global',
    sourceLabel: 'Global Default (Error Fallback)'
  };
};
